/**
 * LockBox3 Browser-Compatible TypeScript Implementation
 * Uses crypto-js library for browser compatibility with full type definitions
 */

import * as CryptoJS from 'crypto-js';

/**
 * Interface for LockBox3 browser functions
 */
export interface LockBox3BrowserFunctions {
    encryptWithSalt(plaintext: string, salt: string): string;
    decryptWithSalt(ciphertext: string, salt: string): string;
    encryptPassword(plaintext: string, salt: string): string;
    decryptPassword(ciphertext: string, salt: string): string;
}

/**
 * Spreads a hash into a buffer using LockBox3's repetition logic
 * @param hash - The hash to spread
 * @param minSize - Minimum size for the output buffer
 * @param maxSize - Maximum size for the output buffer
 * @param outputSeedSize - Target size for the output buffer
 * @returns Buffer with the spread hash
 */
function spreadHashIntoBuffer(
    hash: CryptoJS.lib.WordArray,
    minSize: number,
    maxSize: number,
    outputSeedSize: number
): CryptoJS.lib.WordArray {
    const L = hash.sigBytes;
    
    if (L === outputSeedSize) {
        return hash;
    } else if (L < outputSeedSize) {
        // Extend by repetition
        const extendedWords: number[] = [];
         const wordsNeeded = Math.ceil(outputSeedSize / 4);
        
        let remainingBytes = outputSeedSize;
        let wordIndex = 0;
        
        while (remainingBytes > 0) {
            const bytesToCopy = Math.min(remainingBytes, L);
            const wordsToCopy = Math.ceil(bytesToCopy / 4);
            
             for (let i = 0; i < wordsToCopy && wordIndex < wordsNeeded; i++) {
                 const sourceIndex = i % hash.words.length;
                 extendedWords[wordIndex] = hash.words[sourceIndex];
                 wordIndex++;
             }
            
            remainingBytes -= bytesToCopy;
        }
        
        return CryptoJS.lib.WordArray.create(extendedWords, outputSeedSize);
    } else {
        // Truncate
        return CryptoJS.lib.WordArray.create(
            hash.words.slice(0, Math.ceil(outputSeedSize / 4)), 
            outputSeedSize
        );
    }
}

/**
 * Derives a key from salt using LockBox3's key derivation function
 * @param salt - The salt string
 * @returns The derived key
 */
function deriveKey(salt: string): CryptoJS.lib.WordArray {
    // Convert salt to UTF-8 bytes (equivalent to TMBCSEncoding in Delphi 12)
    const saltBytes = CryptoJS.enc.Utf8.parse(salt);
    
     // Calculate InputSeedSize = Length(ASalt) * SizeOf(Char)
     // In Delphi 12, SizeOf(Char) = 2, but we use UTF-8 encoding
    
    // Create a buffer with the salt bytes
    const seedBuffer = saltBytes;
    
    // Hash the seed buffer using SHA1
    const hash = CryptoJS.SHA1(seedBuffer);
    
    // Spread the hash into a 32-byte buffer for AES-256
    const key = spreadHashIntoBuffer(hash, 32, 32, 32);
    
    return key;
}

/**
 * Encrypts a string using LockBox3 methodology (Browser version)
 * @param plaintext - The text to encrypt
 * @param salt - The salt to use for encryption
 * @returns Base64 encoded encrypted text
 */
export function encryptWithSalt(plaintext: string, salt: string): string {
    // Convert plaintext to UTF-8 bytes
    const plaintextBuffer = CryptoJS.enc.Utf8.parse(plaintext);

    // LockBox3 adds a 0x80 byte to the plaintext before encryption
    const plaintextWithMarker = CryptoJS.lib.WordArray.create();
    plaintextWithMarker.concat(plaintextBuffer);
    
    // Add 0x80 marker byte
    const markerWord = CryptoJS.lib.WordArray.create([0x80000000], 1);
    plaintextWithMarker.concat(markerWord);

    // Apply zero padding to reach AES block size
    const blockSize = 16; // AES block size
    const paddingLength = blockSize - (plaintextWithMarker.sigBytes % blockSize);
    
    if (paddingLength < blockSize) {
        const padding = CryptoJS.lib.WordArray.create([], paddingLength);
        plaintextWithMarker.concat(padding);
    }

    // Derive key from salt
    const key = deriveKey(salt);

    // Encrypt using AES-256-ECB
    const encrypted = CryptoJS.AES.encrypt(plaintextWithMarker, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.NoPadding
    });

    return encrypted.toString();
}

/**
 * Decrypts a string using LockBox3 methodology (Browser version)
 * @param ciphertext - Base64 encoded encrypted text
 * @param salt - The salt used for encryption
 * @returns The decrypted text
 */
export function decryptWithSalt(ciphertext: string, salt: string): string {
    // Decode base64 ciphertext
    const encryptedBuffer = CryptoJS.enc.Base64.parse(ciphertext);
    
    // LockBox3 may add a 4-byte header to some ciphertexts
    // Check if the data length is a multiple of 16 after removing 4 bytes
    let actualEncryptedData = encryptedBuffer;
    if (encryptedBuffer.sigBytes >= 4 && (encryptedBuffer.sigBytes - 4) % 16 === 0) {
        // Remove 4-byte header
        const headerRemoved = CryptoJS.lib.WordArray.create(
            encryptedBuffer.words.slice(1), // Remove first word (4 bytes)
            encryptedBuffer.sigBytes - 4
        );
        actualEncryptedData = headerRemoved;
    }

    // Derive key from salt
    const key = deriveKey(salt);

    // Decrypt using AES-256-ECB
    const decrypted = CryptoJS.AES.decrypt(
        actualEncryptedData.toString(CryptoJS.enc.Base64),
        key,
        {
            mode: CryptoJS.mode.ECB,
            padding: CryptoJS.pad.NoPadding
        }
    );

    // Get the decrypted bytes directly
    const decryptedBuffer = decrypted;

    // Handle LockBox3's special padding scheme
    let actualDataLength = decryptedBuffer.sigBytes;
    for (let i = 0; i < decryptedBuffer.sigBytes; i++) {
        const byteIndex = Math.floor(i / 4);
        const wordIndex = i % 4;
        const byte = (decryptedBuffer.words[byteIndex] >>> (24 - wordIndex * 8)) & 0xFF;
        
        if (byte === 0) {
            actualDataLength = i;
            break;
        }
    }
    
    // Extract actual data
    const actualDataWords: number[] = [];
    const wordsNeeded = Math.ceil(actualDataLength / 4);
    
    for (let i = 0; i < wordsNeeded; i++) {
        actualDataWords[i] = decryptedBuffer.words[i];
    }
    
    let actualData = CryptoJS.lib.WordArray.create(actualDataWords, actualDataLength);
    
    // LockBox3 seems to add a special byte (0x80) at the end
    // Remove it if present
    if (actualData.sigBytes > 0) {
        const lastByte = (actualData.words[Math.floor((actualData.sigBytes - 1) / 4)] >>> (24 - ((actualData.sigBytes - 1) % 4) * 8)) & 0xFF;
        if (lastByte === 0x80) {
            actualData.sigBytes--;
        }
    }

    // Convert back to string using UTF-8 encoding
    return actualData.toString(CryptoJS.enc.Utf8);
}

/**
 * Legacy function name for encryptWithSalt
 * @param plaintext - The text to encrypt
 * @param salt - The salt to use for encryption
 * @returns Base64 encoded encrypted text
 */
export function encryptPassword(plaintext: string, salt: string): string {
    return encryptWithSalt(plaintext, salt);
}

/**
 * Legacy function name for decryptWithSalt
 * @param ciphertext - Base64 encoded encrypted text
 * @param salt - The salt used for encryption
 * @returns The decrypted text
 */
export function decryptPassword(ciphertext: string, salt: string): string {
    return decryptWithSalt(ciphertext, salt);
}

// Export the main functions as default
export default {
    encryptWithSalt,
    decryptWithSalt,
    encryptPassword,
    decryptPassword
} as LockBox3BrowserFunctions;

// Browser global object (for non-module usage)
if (typeof window !== 'undefined') {
    (window as any).LockBox3 = {
        encryptWithSalt,
        decryptWithSalt,
        encryptPassword,
        decryptPassword
    };
}
